import { z } from 'zod';
import { richTextDocWireSchema } from '../richtext/schema';
import {
  FOLLOWUP_KINDS,
  TASK_ACTIONS,
  TASK_CREATED_VIA,
  TASK_FIELD_LIMITS as L,
  awaitingPartySchema,
  nonWorkingDayRuleSchema,
  recurrenceStatusSchema,
  taskEventTypeSchema,
  taskKindSchema,
  taskPrioritySchema,
  taskSourceTypeSchema,
  taskStatusSchema,
  taskVisibilitySchema,
} from '../tasks';
import {
  commaList,
  isoDateTimeSchema,
  localDateSchema,
  multiLineText,
  queryBooleanSchema,
  singleLineText,
  uuidSchema,
} from './common';
import { messageDtoSchema } from './messaging';
import { memberDtoSchema } from './org';
import { pageInfoSchema, paginated, paginationQuerySchema } from './pagination';
import { recurrenceRuleSchema } from './recurrence';

/**
 * Tasks and delegation contracts (SPEC §6, PLAN §4 "Later modules — Tasks and delegation", §3.2;
 * D-037, D-054, D-055, D-059, D-071). The API is a thin layer over `@orbit/domain/tasks`: one
 * explicit action endpoint per transition (`POST /tasks/:taskId/<action>`, see
 * `TASK_ACTION_PATHS` in `@orbit/shared/tasks`), never a generic status PATCH.
 *
 * - Task paths accept the UUID or the human number (`/tasks/T-1042`, {@link taskRefSchema}).
 * - Every action and `PATCH` accepts `If-Match: "<version>"` (`412 version_conflict`, D-071) and
 *   an optional `Idempotency-Key`; every action answers `{ task }` ({@link taskResponseSchema}).
 * - Dates are workspace-local `YYYY-MM-DD`, times `HH:mm`; instants are ISO-8601 UTC.
 * - The rich-text description is ProseMirror JSON (D-045); `descriptionText` is server-derived.
 *
 * Owned by the Phase 4 tasks workstream (`apps/api/src/modules/tasks`); projects are in
 * `./projects.ts`, the follow-up automation settings at the end of this file.
 */

// ================================================================ primitives

/** A task path parameter: UUID or `T-1042` / `1042` (resolved by the service). */
export const taskRefSchema = z
  .string()
  .trim()
  .regex(
    /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|(?:[Tt]-)?\d{1,9})$/i,
    'A task id or number (T-1042)',
  );

export const taskIdParamsSchema = z.object({ taskId: taskRefSchema });
export type TaskIdParams = z.infer<typeof taskIdParamsSchema>;

export const taskUserParamsSchema = z.object({ taskId: taskRefSchema, userId: uuidSchema });
export type TaskUserParams = z.infer<typeof taskUserParamsSchema>;

export const taskChecklistItemParamsSchema = z.object({
  taskId: taskRefSchema,
  itemId: uuidSchema,
});
export type TaskChecklistItemParams = z.infer<typeof taskChecklistItemParamsSchema>;

export const taskFileParamsSchema = z.object({ taskId: taskRefSchema, fileId: uuidSchema });
export type TaskFileParams = z.infer<typeof taskFileParamsSchema>;

/** `HH:mm`, 24 h, workspace time. */
export const clockTimeSchema = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm (24 h)');

/** `If-Match: "<version>"` (quotes optional). Parse with {@link parseIfMatch}. */
export const ifMatchValueSchema = z.string().regex(/^\s*(?:W\/)?"?\d{1,9}"?\s*$/);

/** Headers of the task write endpoints (PATCH and every action). */
export const taskWriteHeadersSchema = z.looseObject({
  'if-match': ifMatchValueSchema.optional(),
  'idempotency-key': z
    .string()
    .regex(/^[A-Za-z0-9_-]{8,64}$/)
    .optional(),
});
export type TaskWriteHeaders = z.infer<typeof taskWriteHeadersSchema>;

/** `"7"` / `7` / `W/"7"` → 7; absent → null (last write wins, D-071). */
export function parseIfMatch(value: string | undefined): number | null {
  if (value === undefined) return null;
  const match = /(\d{1,9})/.exec(value);
  return match ? Number(match[1]) : null;
}

export const taskTitleSchema = singleLineText(1, L.titleMax);
/** Length only; the ≥ 3 characters rule answers `422 definition_of_done_required`. */
export const definitionOfDoneSchema = multiLineText(1, L.definitionOfDoneMax);
export const taskReasonSchema = multiLineText(1, L.reasonMax);
export const taskNoteSchema = multiLineText(1, L.noteMax);

// ================================================================ DTOs

export const taskSourceDtoSchema = z.object({
  type: taskSourceTypeSchema,
  /** The source row (message, thread root, meeting occurrence, report, transcript, review). */
  id: uuidSchema,
  conversationId: uuidSchema.nullable(),
  /** `/c/{conversationId}/m/{messageId}` for message sources the viewer can read, else null. */
  permalink: z.string().nullable(),
});
export type TaskSourceDto = z.infer<typeof taskSourceDtoSchema>;

export const taskPushBackDtoSchema = z.object({
  id: uuidSchema,
  proposedDueDate: localDateSchema.nullable(),
  proposedDueTime: clockTimeSchema.nullable(),
  question: z.string().nullable(),
  raisedByUserId: uuidSchema,
  at: isoDateTimeSchema,
});
export type TaskPushBackDto = z.infer<typeof taskPushBackDtoSchema>;

export const taskBlockerDtoSchema = z.object({
  note: z.string(),
  blockedByUserId: uuidSchema.nullable(),
  blockedByTaskId: uuidSchema.nullable(),
  /** `T-1042` of the blocking task when the viewer can see it. */
  blockedByTaskNumber: z.string().nullable(),
  since: isoDateTimeSchema,
});
export type TaskBlockerDto = z.infer<typeof taskBlockerDtoSchema>;

export const decisionOptionDtoSchema = z.object({
  id: uuidSchema,
  position: z.int().min(1).max(L.decisionOptionsMax),
  label: z.string(),
  details: z.string().nullable(),
});
export type DecisionOptionDto = z.infer<typeof decisionOptionDtoSchema>;

/** `decision_requests` for `kind = 'decision_request'`; the deadline is the task's due date/time. */
export const taskDecisionDtoSchema = z.object({
  context: z.string(),
  options: z.array(decisionOptionDtoSchema),
  recommendation: z.string(),
  recommendedOptionId: uuidSchema.nullable(),
  decidedOptionId: uuidSchema.nullable(),
  decisionNote: z.string().nullable(),
  decidedAt: isoDateTimeSchema.nullable(),
  decidedByUserId: uuidSchema.nullable(),
  /**
   * The requester chose to post the decision itself (the option and the note) in the source
   * thread when it is made; otherwise the post says `T-n · Decided` and nothing more (D-386).
   */
  publishDecisionToSource: z.boolean(),
});
export type TaskDecisionDto = z.infer<typeof taskDecisionDtoSchema>;

/** One `task_due_date_changes` row (SPEC §6.1 `due_date_history[]`, D-059). */
export const dueDateChangeDtoSchema = z.object({
  id: uuidSchema,
  from: localDateSchema.nullable(),
  fromTime: clockTimeSchema.nullable(),
  to: localDateSchema.nullable(),
  toTime: clockTimeSchema.nullable(),
  reason: z.string().nullable(),
  phase: z.enum(['pre_acceptance', 'post_acceptance']),
  direction: z.enum(['set', 'later', 'earlier', 'time_only', 'cleared']),
  initiatedBy: z.enum(['owner', 'delegator', 'admin', 'system']),
  countsAsSlip: z.boolean(),
  /** `slip_count` after this change when it counts. */
  slipNumber: z.int().nullable(),
  changedBy: uuidSchema.nullable(),
  changedAt: isoDateTimeSchema,
});
export type DueDateChangeDto = z.infer<typeof dueDateChangeDtoSchema>;

export const checklistProgressDtoSchema = z.object({
  done: z.int().nonnegative(),
  total: z.int().nonnegative(),
});

/** `TaskDto.kra`: the KRA a task is tagged with (Phase 5 `kras`, D-360). */
export const taskKraRefDtoSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  positionId: uuidSchema,
  status: z.enum(['active', 'archived']),
});
export type TaskKraRefDto = z.infer<typeof taskKraRefDtoSchema>;

/**
 * The full task (`GET /tasks/:taskId`, every action's response). Column names and enums follow
 * the `tasks` data model; {@link taskSummaryDtoSchema} is the realtime subset.
 */
export const taskDtoSchema = z.object({
  id: uuidSchema,
  /** `T-1042` (per-workspace counter, D-003). */
  number: z.string(),
  kind: taskKindSchema,
  title: z.string(),
  description: richTextDocWireSchema.nullable(),
  descriptionText: z.string().nullable(),
  definitionOfDone: z.string().nullable(),
  ownerUserId: uuidSchema,
  createdByUserId: uuidSchema,
  /** Null = a self task; for a decision request, the requester. */
  delegatedByUserId: uuidSchema.nullable(),
  status: taskStatusSchema,
  /** Push-back sub-state of `proposed` (D-055); null otherwise. */
  awaitingParty: awaitingPartySchema.nullable(),
  priority: taskPrioritySchema,
  visibility: taskVisibilitySchema,
  postUpdatesToSource: z.boolean(),
  dueDate: localDateSchema.nullable(),
  dueTime: clockTimeSchema.nullable(),
  /** The deadline instant (`due_date 23:59:59` local without a time). */
  dueAt: isoDateTimeSchema.nullable(),
  departmentId: uuidSchema.nullable(),
  projectId: uuidSchema.nullable(),
  milestoneId: uuidSchema.nullable(),
  /** KRA tag (SPEC §7.2): a KRA of a position the owner holds (D-202, D-360). */
  kraId: uuidSchema.nullable(),
  /**
   * The tagged KRA's name for the task panel (D-360); null without a tag. Optional: builders that
   * do not load it yet leave it out and the panel falls back to "Tagged to a KRA".
   */
  kra: taskKraRefDtoSchema.nullable().optional(),
  watcherIds: z.array(uuidSchema),
  /** Computed for the viewer (advisory: the server re-checks every action). */
  allowedActions: z.array(z.enum(TASK_ACTIONS)),
  slipCount: z.int().nonnegative(),
  reopenCount: z.int().nonnegative(),
  dueDateHistory: z.array(dueDateChangeDtoSchema),
  pushBack: taskPushBackDtoSchema.nullable(),
  blocker: taskBlockerDtoSchema.nullable(),
  decision: taskDecisionDtoSchema.nullable(),
  /** Null when there is none or the viewer cannot read it. */
  source: taskSourceDtoSchema.nullable(),
  createdVia: z.enum(TASK_CREATED_VIA),
  recurrenceId: uuidSchema.nullable(),
  checklistProgress: checklistProgressDtoSchema,
  attachmentFileIds: z.array(uuidSchema),
  /** The hidden `kind = 'task'` comment conversation (D-054); null while Draft. */
  conversationId: uuidSchema.nullable(),
  completionNote: z.string().nullable(),
  declineReason: z.string().nullable(),
  cancelReason: z.string().nullable(),
  proposedAt: isoDateTimeSchema.nullable(),
  acceptedAt: isoDateTimeSchema.nullable(),
  startedAt: isoDateTimeSchema.nullable(),
  completedAt: isoDateTimeSchema.nullable(),
  verifiedAt: isoDateTimeSchema.nullable(),
  verifiedByUserId: uuidSchema.nullable(),
  declinedAt: isoDateTimeSchema.nullable(),
  cancelledAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  /** Optimistic concurrency (`If-Match`, D-071). */
  version: z.int().positive(),
});
export type TaskDto = z.infer<typeof taskDtoSchema>;

/** PLAN §5 `TaskSummaryDTO`: realtime payloads and list rows that need no description. */
export const taskSummaryDtoSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  kind: taskKindSchema,
  title: z.string(),
  status: taskStatusSchema,
  awaitingParty: awaitingPartySchema.nullable(),
  ownerUserId: uuidSchema,
  delegatedByUserId: uuidSchema.nullable(),
  createdByUserId: uuidSchema,
  priority: taskPrioritySchema,
  dueDate: localDateSchema.nullable(),
  dueTime: clockTimeSchema.nullable(),
  slipCount: z.int().nonnegative(),
  visibility: taskVisibilitySchema,
  departmentId: uuidSchema.nullable(),
  projectId: uuidSchema.nullable(),
  milestoneId: uuidSchema.nullable(),
  kraId: uuidSchema.nullable(),
  conversationId: uuidSchema.nullable(),
  updatedAt: isoDateTimeSchema,
  version: z.int().positive(),
});
export type TaskSummaryDto = z.infer<typeof taskSummaryDtoSchema>;

export const taskResponseSchema = z.object({ task: taskDtoSchema });
export type TaskResponse = z.infer<typeof taskResponseSchema>;

// ================================================================ create and edit

export const taskSourceInputSchema = z.strictObject({
  /** From a message: the message itself; from a thread: its root. */
  type: z.enum(['message', 'thread']),
  messageId: uuidSchema,
});
export type TaskSourceInput = z.infer<typeof taskSourceInputSchema>;

export const checklistItemInputSchema = z.strictObject({
  title: singleLineText(1, L.checklistTitleMax),
  ownerUserId: uuidSchema.nullable().optional(),
  dueDate: localDateSchema.nullable().optional(),
  dueTime: clockTimeSchema.nullable().optional(),
});
export type ChecklistItemInput = z.infer<typeof checklistItemInputSchema>;

export const decisionOptionInputSchema = z.strictObject({
  label: singleLineText(1, L.decisionOptionLabelMax),
  details: multiLineText(1, L.decisionOptionDetailsMax).nullable().optional(),
});

/** Decision-request fields (SPEC §6.3); lengths beyond these answer `422 decision_fields_required`. */
export const decisionRequestInputSchema = z.strictObject({
  context: multiLineText(1, L.decisionContextMax),
  options: z.array(decisionOptionInputSchema).min(1).max(L.decisionOptionsMax),
  recommendation: multiLineText(1, L.decisionRecommendationMax),
  /** 1-based position in `options`. */
  recommendedOptionPosition: z.int().min(1).max(L.decisionOptionsMax).nullable().optional(),
  /**
   * Post the decision (the chosen option and the note) back into the source thread when it is
   * made, on top of the request's own number and status. Default false: the post then carries
   * the number and status only (D-386).
   */
  publishDecisionToSource: z.boolean().optional(),
});
export type DecisionRequestInputDto = z.infer<typeof decisionRequestInputSchema>;

/** The recurrence of `POST /tasks` and `POST /task-recurrences`. */
export const taskRecurrenceInputSchema = z.strictObject({
  rule: recurrenceRuleSchema,
  /** Default: the task's due date, else today. */
  dtstartDate: localDateSchema.optional(),
  /** Create each instance N days early (default by frequency: daily 0, weekly 2, longer 5). */
  leadDays: z.int().min(0).max(30).optional(),
  nonWorkingDayRule: nonWorkingDayRuleSchema.optional(),
});
export type TaskRecurrenceInput = z.infer<typeof taskRecurrenceInputSchema>;

/** Client-settable `created_via` values (the rest are set by the server paths that use them). */
export const CLIENT_TASK_CREATED_VIA = [
  'web',
  'desktop',
  'mobile',
  'quick_add',
  'message_action',
  /** A `/task` line typed in the composer (the web's `/task` path; D-384). */
  'slash_command',
] as const;

export const createTaskRequestSchema = z.strictObject({
  kind: taskKindSchema.default('task'),
  title: taskTitleSchema,
  ownerUserId: uuidSchema,
  /** Required (≥ 3 characters) when the owner is someone else. */
  definitionOfDone: definitionOfDoneSchema.nullable().optional(),
  /** Required when delegating, and for decision requests (the decision deadline). */
  dueDate: localDateSchema.nullable().optional(),
  dueTime: clockTimeSchema.nullable().optional(),
  description: richTextDocWireSchema.nullable().optional(),
  priority: taskPrioritySchema.optional(),
  /** Default: the department's `defaultTaskVisibility`; `participants` for decision requests. */
  visibility: taskVisibilitySchema.optional(),
  postUpdatesToSource: z.boolean().optional(),
  watcherIds: z.array(uuidSchema).max(L.watchersMax).optional(),
  /** Default: the owner's department (D-007). */
  departmentId: uuidSchema.nullable().optional(),
  projectId: uuidSchema.nullable().optional(),
  milestoneId: uuidSchema.nullable().optional(),
  kraId: uuidSchema.nullable().optional(),
  source: taskSourceInputSchema.optional(),
  checklist: z.array(checklistItemInputSchema).max(L.checklistMax).optional(),
  /** Files the caller uploaded (D-111). */
  attachmentFileIds: z.array(uuidSchema).max(L.attachmentsMax).optional(),
  recurrence: taskRecurrenceInputSchema.optional(),
  decision: decisionRequestInputSchema.optional(),
  /** Save as Draft (visible only to the creator). */
  asDraft: z.boolean().optional(),
  createdVia: z.enum(CLIENT_TASK_CREATED_VIA).optional(),
});
export type CreateTaskRequest = z.input<typeof createTaskRequestSchema>;
export type CreateTaskBody = z.output<typeof createTaskRequestSchema>;

/**
 * `PATCH /tasks/:taskId`: fields only (the due date, owner and status have their own actions).
 * Who may change what follows PLAN §3.2 "Field edit permissions" (`canEditTaskField`).
 */
export const updateTaskRequestSchema = z
  .strictObject({
    title: taskTitleSchema.optional(),
    description: richTextDocWireSchema.nullable().optional(),
    definitionOfDone: definitionOfDoneSchema.nullable().optional(),
    priority: taskPrioritySchema.optional(),
    departmentId: uuidSchema.nullable().optional(),
    projectId: uuidSchema.nullable().optional(),
    milestoneId: uuidSchema.nullable().optional(),
    kraId: uuidSchema.nullable().optional(),
    visibility: taskVisibilitySchema.optional(),
    postUpdatesToSource: z.boolean().optional(),
    /** Decision requests while Draft or awaiting the requester (PLAN §3.2). */
    decision: decisionRequestInputSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, 'Change at least one field');
export type UpdateTaskRequest = z.infer<typeof updateTaskRequestSchema>;

// ================================================================ actions (PLAN §3.2 tables)

const optionalComment = { comment: taskNoteSchema.optional() };

export const sendTaskRequestSchema = z.strictObject({ ...optionalComment });
export const acceptTaskRequestSchema = z.strictObject({
  /** Recurring instances: also accept the series (default true). */
  acceptSeries: z.boolean().optional(),
  ...optionalComment,
});
export const pushBackTaskRequestSchema = z.strictObject({
  proposedDueDate: localDateSchema.nullable().optional(),
  proposedDueTime: clockTimeSchema.nullable().optional(),
  question: taskNoteSchema.nullable().optional(),
});
export const respondPushbackRequestSchema = z.strictObject({
  acceptProposedDate: z.boolean(),
  /** Required when not agreeing to the proposed date. */
  answer: taskNoteSchema.nullable().optional(),
  note: taskNoteSchema.nullable().optional(),
  newDueDate: localDateSchema.nullable().optional(),
  newDueTime: clockTimeSchema.nullable().optional(),
  definitionOfDone: definitionOfDoneSchema.nullable().optional(),
  decision: decisionRequestInputSchema.optional(),
});
export const declineTaskRequestSchema = z.strictObject({ reason: taskReasonSchema });
export const reassignTaskRequestSchema = z.strictObject({
  ownerUserId: uuidSchema,
  /** Required once the task was accepted. */
  reason: taskReasonSchema.nullable().optional(),
  definitionOfDone: definitionOfDoneSchema.nullable().optional(),
  /** Only for an undated self task. */
  dueDate: localDateSchema.nullable().optional(),
  dueTime: clockTimeSchema.nullable().optional(),
  keepPreviousOwnerAsWatcher: z.boolean().optional(),
});
export const startTaskRequestSchema = z.strictObject({ ...optionalComment });
export const blockTaskRequestSchema = z.strictObject({
  note: taskNoteSchema,
  blockedByUserId: uuidSchema.nullable().optional(),
  blockedByTaskId: uuidSchema.nullable().optional(),
});
export const unblockTaskRequestSchema = z.strictObject({
  note: taskNoteSchema.nullable().optional(),
});
export const completeTaskRequestSchema = z.strictObject({
  completionNote: taskNoteSchema.nullable().optional(),
  /** Uploaded by the owner (D-111); attached with `purpose = 'evidence'`. */
  evidenceFileIds: z.array(uuidSchema).max(L.attachmentsMax).optional(),
  closeOpenChecklistItems: z.boolean().optional(),
});
export const verifyTaskRequestSchema = z.strictObject({
  note: taskNoteSchema.nullable().optional(),
});
export const reopenTaskRequestSchema = z.strictObject({
  comment: taskNoteSchema,
  newDueDate: localDateSchema.nullable().optional(),
  newDueTime: clockTimeSchema.nullable().optional(),
});
export const cancelTaskRequestSchema = z.strictObject({ reason: taskReasonSchema });
export const changeDueDateRequestSchema = z.strictObject({
  /** Null clears the date (self tasks only). */
  dueDate: localDateSchema.nullable(),
  dueTime: clockTimeSchema.nullable().optional(),
  /** Required after acceptance (≥ 3 characters). */
  reason: taskReasonSchema.nullable().optional(),
});
export const decideTaskRequestSchema = z.strictObject({
  optionId: uuidSchema.nullable().optional(),
  note: taskNoteSchema.nullable().optional(),
});

/** The body schema of each action endpoint, keyed by `TaskAction`. */
export const TASK_ACTION_REQUEST_SCHEMAS = {
  send: sendTaskRequestSchema,
  accept: acceptTaskRequestSchema,
  push_back: pushBackTaskRequestSchema,
  respond_pushback: respondPushbackRequestSchema,
  decline: declineTaskRequestSchema,
  reassign: reassignTaskRequestSchema,
  start: startTaskRequestSchema,
  block: blockTaskRequestSchema,
  unblock: unblockTaskRequestSchema,
  complete: completeTaskRequestSchema,
  verify: verifyTaskRequestSchema,
  reopen: reopenTaskRequestSchema,
  cancel: cancelTaskRequestSchema,
  change_due_date: changeDueDateRequestSchema,
  decide: decideTaskRequestSchema,
} as const;
export type TaskActionRequest<A extends keyof typeof TASK_ACTION_REQUEST_SCHEMAS> = z.infer<
  (typeof TASK_ACTION_REQUEST_SCHEMAS)[A]
>;

// ================================================================ history, watchers, checklist, attachments

export const taskEventDtoSchema = z.object({
  id: uuidSchema,
  at: isoDateTimeSchema,
  kind: taskEventTypeSchema,
  actorKind: z.enum(['user', 'system', 'bot']),
  actorUserId: uuidSchema.nullable(),
  from: taskStatusSchema.nullable(),
  to: taskStatusSchema.nullable(),
  ownerUserId: uuidSchema,
  reason: z.string().nullable(),
  /** Changed fields `{ field: { old, new } }`, push-back contents, follow-up ids. */
  payload: z.record(z.string(), z.unknown()),
  /** The system message in the task thread. */
  messageId: uuidSchema.nullable(),
});
export type TaskEventDto = z.infer<typeof taskEventDtoSchema>;

export const listTaskEventsQuerySchema = paginationQuerySchema;
export const taskEventListResponseSchema = paginated(taskEventDtoSchema);

export const taskChecklistItemDtoSchema = z.object({
  id: uuidSchema,
  taskId: uuidSchema,
  title: z.string(),
  done: z.boolean(),
  ownerUserId: uuidSchema.nullable(),
  dueDate: localDateSchema.nullable(),
  dueTime: clockTimeSchema.nullable(),
  position: z.int().nonnegative(),
  doneAt: isoDateTimeSchema.nullable(),
  doneByUserId: uuidSchema.nullable(),
  createdByUserId: uuidSchema,
});
export type TaskChecklistItemDto = z.infer<typeof taskChecklistItemDtoSchema>;

export const checklistItemListResponseSchema = z.object({
  items: z.array(taskChecklistItemDtoSchema),
});
export const createChecklistItemRequestSchema = checklistItemInputSchema;
export const updateChecklistItemRequestSchema = z
  .strictObject({
    title: singleLineText(1, L.checklistTitleMax).optional(),
    done: z.boolean().optional(),
    ownerUserId: uuidSchema.nullable().optional(),
    dueDate: localDateSchema.nullable().optional(),
    dueTime: clockTimeSchema.nullable().optional(),
    /** 0-based; other items shift. */
    position: z.int().min(0).max(L.checklistMax).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, 'Change at least one field');
export const checklistItemResponseSchema = z.object({ item: taskChecklistItemDtoSchema });

export const addTaskAttachmentRequestSchema = z.strictObject({
  fileId: uuidSchema,
  purpose: z.enum(['reference', 'evidence']).optional(),
});

// ================================================================ recurrences

/** The task fields a recurrence copies into each instance (the `POST /tasks` subset). */
export const taskTemplateSchema = z.strictObject({
  title: taskTitleSchema,
  ownerUserId: uuidSchema,
  definitionOfDone: definitionOfDoneSchema.nullable().optional(),
  description: richTextDocWireSchema.nullable().optional(),
  priority: taskPrioritySchema.optional(),
  visibility: taskVisibilitySchema.optional(),
  departmentId: uuidSchema.nullable().optional(),
  projectId: uuidSchema.nullable().optional(),
  kraId: uuidSchema.nullable().optional(),
  watcherIds: z.array(uuidSchema).max(L.watchersMax).optional(),
  checklist: z
    .array(
      z.strictObject({
        title: singleLineText(1, L.checklistTitleMax),
        ownerUserId: uuidSchema.nullable().optional(),
        dueOffsetDays: z.int().min(-30).max(30).nullable().optional(),
      }),
    )
    .max(L.checklistMax)
    .optional(),
});
export type TaskTemplate = z.infer<typeof taskTemplateSchema>;

export const taskRecurrenceDtoSchema = z.object({
  id: uuidSchema,
  rule: recurrenceRuleSchema,
  dtstartDate: localDateSchema,
  dueTime: clockTimeSchema.nullable(),
  leadDays: z.int().min(0).max(30),
  nonWorkingDayRule: nonWorkingDayRuleSchema,
  status: recurrenceStatusSchema,
  template: taskTemplateSchema,
  ownerUserId: uuidSchema,
  delegatedByUserId: uuidSchema.nullable(),
  seriesAcceptedAt: isoDateTimeSchema.nullable(),
  lastGeneratedOccurrenceDate: localDateSchema.nullable(),
  nextOccurrenceDate: localDateSchema.nullable(),
  createdByUserId: uuidSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  version: z.int().positive(),
});
export type TaskRecurrenceDto = z.infer<typeof taskRecurrenceDtoSchema>;

export const recurrenceIdParamsSchema = z.object({ recurrenceId: uuidSchema });
export const createTaskRecurrenceRequestSchema = z.strictObject({
  template: taskTemplateSchema,
  rule: recurrenceRuleSchema,
  dtstartDate: localDateSchema,
  dueTime: clockTimeSchema.nullable().optional(),
  leadDays: z.int().min(0).max(30).optional(),
  nonWorkingDayRule: nonWorkingDayRuleSchema.optional(),
});
export const updateTaskRecurrenceRequestSchema = z
  .strictObject({
    status: recurrenceStatusSchema.optional(),
    rule: recurrenceRuleSchema.optional(),
    template: taskTemplateSchema.optional(),
    dueTime: clockTimeSchema.nullable().optional(),
    leadDays: z.int().min(0).max(30).optional(),
    nonWorkingDayRule: nonWorkingDayRuleSchema.optional(),
    endedReason: taskReasonSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, 'Change at least one field');
export const listTaskRecurrencesQuerySchema = paginationQuerySchema.extend({
  status: commaList(recurrenceStatusSchema).optional(),
  ownerUserId: uuidSchema.optional(),
});
export const taskRecurrenceListResponseSchema = paginated(taskRecurrenceDtoSchema);
export const taskRecurrenceResponseSchema = z.object({ recurrence: taskRecurrenceDtoSchema });

// ================================================================ lists and views (SPEC §6.6)

export const TASK_SORTS = ['due', 'priority', 'created', 'updated'] as const;
export type TaskSort = (typeof TASK_SORTS)[number];

/** The `/tasks` filters, shared by the board and project views. */
export const taskFiltersSchema = z.object({
  ownerUserId: commaList(uuidSchema, { max: 50 }).optional(),
  delegatedByUserId: uuidSchema.optional(),
  kind: taskKindSchema.optional(),
  status: commaList(taskStatusSchema).optional(),
  priority: commaList(taskPrioritySchema).optional(),
  projectId: uuidSchema.optional(),
  milestoneId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  kraId: uuidSchema.optional(),
  dueFrom: localDateSchema.optional(),
  dueTo: localDateSchema.optional(),
  /** Full-text over title, description and definition of done (D-050 normalisation). */
  q: singleLineText(1, 200).optional(),
});
export type TaskFilters = z.infer<typeof taskFiltersSchema>;

export const listTasksQuerySchema = paginationQuerySchema
  .extend(taskFiltersSchema.shape)
  .extend({ sort: z.enum(TASK_SORTS).default('due') });
export type ListTasksQuery = z.infer<typeof listTasksQuerySchema>;
export const taskListResponseSchema = paginated(taskDtoSchema);

/**
 * GET /conversations/:conversationId/tasks (D-1133) — the Tasks tab inside a chat.
 *
 * The global `/tasks` list answers "what is on my plate"; this answers "what came out of this
 * conversation", which is the question somebody scrolling a channel actually has. It is not a
 * `conversationId` filter on `listTasksQuerySchema` because the two have different access
 * gates: this one checks the CONVERSATION first (`assertCanRead` → 404, so a channel you cannot
 * see does not even admit how many tasks it has), and only then applies the ordinary task
 * visibility filter. Both must hold.
 *
 * `open` is the default because an archive of done work is not what a channel's Tasks tab is
 * for; `all` is there for when it is.
 */
export const CONVERSATION_TASK_SCOPES = ['open', 'all'] as const;
export type ConversationTaskScope = (typeof CONVERSATION_TASK_SCOPES)[number];

export const conversationTasksQuerySchema = paginationQuerySchema.extend({
  scope: z.enum(CONVERSATION_TASK_SCOPES).default('open'),
  sort: z.enum(TASK_SORTS).default('due'),
});
export type ConversationTasksQuery = z.infer<typeof conversationTasksQuerySchema>;
export const conversationTasksResponseSchema = paginated(taskDtoSchema);
export type ConversationTasksResponse = z.infer<typeof conversationTasksResponseSchema>;

export const MY_TASK_BUCKETS = ['today', 'upcoming', 'overdue', 'no_date', 'all'] as const;
export type MyTaskBucket = (typeof MY_TASK_BUCKETS)[number];

export const myTasksQuerySchema = paginationQuerySchema.extend({
  bucket: z.enum(MY_TASK_BUCKETS).default('all'),
  sort: z.enum(TASK_SORTS).default('due'),
});
export const myTasksResponseSchema = z.object({
  items: z.array(taskDtoSchema),
  nextCursor: z.string().nullable(),
  page: pageInfoSchema.optional(),
  counts: z.object({
    today: z.int().nonnegative(),
    upcoming: z.int().nonnegative(),
    overdue: z.int().nonnegative(),
    noDate: z.int().nonnegative(),
    /** Proposed to me and my move (`awaiting_party = 'owner'`). */
    awaitingAcceptance: z.int().nonnegative(),
  }),
});
export type MyTasksResponse = z.infer<typeof myTasksResponseSchema>;

export const DUE_STATUSES = ['on_time', 'due_today', 'overdue', 'no_date'] as const;
export type DueStatusDto = (typeof DUE_STATUSES)[number];

export const delegatedTasksQuerySchema = z.object({
  /** Also Verified, Declined and Cancelled of the last 14 days. */
  includeClosed: queryBooleanSchema.optional(),
});
export const delegatedTasksResponseSchema = z.object({
  groups: z.array(
    z.object({
      ownerUserId: uuidSchema,
      /** Age of the oldest item, days (groups are sorted by it, oldest first). */
      oldestAgeDays: z.int().nonnegative(),
      tasks: z.array(
        z.object({
          task: taskDtoSchema,
          /** Since accepted; since proposed while Proposed; since done while awaiting verification. */
          ageFrom: z.enum(['accepted', 'proposed', 'completed']),
          ageDays: z.int().nonnegative(),
          /** Proposed tasks: working hours awaiting acceptance. */
          awaitingAcceptanceWorkingHours: z.number().nonnegative().nullable(),
          dueStatus: z.enum(DUE_STATUSES),
          slipCount: z.int().nonnegative(),
        }),
      ),
    }),
  ),
});
export type DelegatedTasksResponse = z.infer<typeof delegatedTasksResponseSchema>;

export const decisionsForMeQuerySchema = z.object({
  /** `decider` (default, "Decisions for me") or `requester` ("I asked"). */
  role: z.enum(['decider', 'requester']).default('decider'),
});
export const decisionsForMeResponseSchema = z.object({
  /** Open decision requests sorted by the decision deadline; overdue ones flagged. */
  items: z.array(z.object({ task: taskDtoSchema, overdue: z.boolean() })),
});

export const BOARD_SCOPES = ['team', 'department'] as const;
export const BOARD_LAYOUTS = ['list', 'kanban'] as const;
export type BoardLayout = (typeof BOARD_LAYOUTS)[number];

export const taskBoardQuerySchema = paginationQuerySchema.extend(taskFiltersSchema.shape).extend({
  /** `team`: my reports' tasks (transitive); `department`: a department's board. */
  scope: z.enum(BOARD_SCOPES).default('department'),
  layout: z.enum(BOARD_LAYOUTS).default('list'),
  sort: z.enum(TASK_SORTS).default('due'),
});
export type TaskBoardQuery = z.infer<typeof taskBoardQuerySchema>;

export const kanbanColumnSchema = z.object({
  status: taskStatusSchema,
  items: z.array(taskDtoSchema),
  /** Total in the column (items are capped at `limit`). */
  count: z.int().nonnegative(),
});

export const taskBoardResponseSchema = z.discriminatedUnion('layout', [
  z.object({
    layout: z.literal('list'),
    items: z.array(taskDtoSchema),
    nextCursor: z.string().nullable(),
    page: pageInfoSchema.optional(),
  }),
  z.object({ layout: z.literal('kanban'), columns: z.array(kanbanColumnSchema) }),
]);
export type TaskBoardResponse = z.infer<typeof taskBoardResponseSchema>;

export const slippingQuerySchema = z.object({
  departmentId: uuidSchema.optional(),
  ownerUserId: uuidSchema.optional(),
  projectId: uuidSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export const slippingResponseSchema = z.object({
  items: z.array(
    z.object({
      task: taskDtoSchema,
      /** Calendar days past the due date (0 when not overdue, slipping by count). */
      overdueDays: z.int().nonnegative(),
    }),
  ),
});

// ================================================================ quick add, message → task, /task

export const parseTaskRequestSchema = z.strictObject({
  text: z.string().min(1).max(L.quickAddMax),
});

export const quickAddTokenDtoSchema = z.object({
  start: z.int().nonnegative(),
  end: z.int().nonnegative(),
  kind: z.enum(['owner', 'date', 'priority', 'project']),
  value: z.string(),
  text: z.string(),
});
export const quickAddWarningDtoSchema = z.object({
  code: z.enum([
    'owner_not_found',
    'owner_ambiguous',
    'multiple_owners',
    'multiple_dates',
    'multiple_priorities',
    'project_not_found',
    'project_ambiguous',
    'date_in_past',
    'invalid_date',
    'empty_title',
    'title_too_long',
  ]),
  text: z.string().optional(),
  /** Member or project ids the text matched. */
  candidateIds: z.array(uuidSchema).optional(),
});

export const taskDraftDtoSchema = z.object({
  title: z.string(),
  ownerUserId: uuidSchema.nullable(),
  dueDate: localDateSchema.nullable(),
  dueTime: clockTimeSchema.nullable(),
  priority: taskPrioritySchema.nullable(),
  projectId: uuidSchema.nullable(),
});
export type TaskDraftDto = z.infer<typeof taskDraftDtoSchema>;

/** `POST /tasks/parse` (deterministic, `@orbit/domain/tasks` `parseQuickAdd`, not AI). */
export const parseTaskResponseSchema = z.object({
  draft: taskDraftDtoSchema,
  tokens: z.array(quickAddTokenDtoSchema),
  warnings: z.array(quickAddWarningDtoSchema),
});
export type ParseTaskResponse = z.infer<typeof parseTaskResponseSchema>;

/**
 * `POST /tasks/draft-from-message`: the "Create task" message action's prefill without AI (the
 * rule-based `TaskDraftProvider`, PLAN §6 Phase 4): title from the message text, owner from the
 * first @mention, due date and priority from phrases (the quick-add grammar). The caller must be
 * able to read the message; nothing is created until they confirm with `POST /tasks`.
 */
export const taskDraftFromMessageRequestSchema = z.strictObject({ messageId: uuidSchema });
export const taskDraftFromMessageResponseSchema = z.object({
  draft: taskDraftDtoSchema.extend({
    definitionOfDone: z.string().nullable(),
    source: taskSourceInputSchema,
    /** Everyone @mentioned (the modal offers them as owner or watchers). */
    mentionedUserIds: z.array(uuidSchema),
  }),
  tokens: z.array(quickAddTokenDtoSchema),
  warnings: z.array(quickAddWarningDtoSchema),
});
export type TaskDraftFromMessageResponse = z.infer<typeof taskDraftFromMessageResponseSchema>;

/**
 * The `/task @owner <title> [by <date>] [p1|p2|p3]` built-in (PLAN §4 slash commands): a task for
 * yourself is created at once (`accepted`, `in_channel`/`ephemeral` confirmation); for anyone else
 * the response is `open_modal` with kind `task_create`, prefilled, because a definition of done is
 * required. The shape follows `POST /conversations/:id/commands` (Phase 2 registry).
 */
export const taskCommandRequestSchema = z.strictObject({
  /** The text after `/task`. */
  text: z.string().max(L.quickAddMax),
  conversationId: uuidSchema,
  threadRootId: uuidSchema.nullable().optional(),
});
export const taskCommandResponseSchema = z.object({
  response: z.object({
    type: z.enum(['ephemeral', 'in_channel', 'open_modal', 'none']),
    text: z.string().nullable(),
    message: messageDtoSchema.nullable(),
    modal: z
      .object({
        kind: z.literal('task_create'),
        prefill: taskDraftDtoSchema.extend({
          source: taskSourceInputSchema.nullable(),
        }),
      })
      .nullable(),
    /** Set when a self task was created. */
    task: taskDtoSchema.nullable(),
  }),
});
export type TaskCommandResponse = z.infer<typeof taskCommandResponseSchema>;

// ================================================================ bulk actions

export const BULK_TASK_ACTIONS = [
  'change_due_date',
  'set_priority',
  'reassign',
  'cancel',
  'complete',
  'add_watcher',
  'move_project',
] as const;
export type BulkTaskAction = (typeof BULK_TASK_ACTIONS)[number];

const bulkTaskIds = z.array(taskRefSchema).min(1).max(L.bulkMax);

/** Each item follows its single-task rules (and its own `If-Match`-free last-write-wins). */
export const bulkTaskRequestSchema = z.discriminatedUnion('action', [
  z.strictObject({
    action: z.literal('change_due_date'),
    taskIds: bulkTaskIds,
    params: changeDueDateRequestSchema,
  }),
  z.strictObject({
    action: z.literal('set_priority'),
    taskIds: bulkTaskIds,
    params: z.strictObject({ priority: taskPrioritySchema }),
  }),
  z.strictObject({
    action: z.literal('reassign'),
    taskIds: bulkTaskIds,
    params: reassignTaskRequestSchema,
  }),
  z.strictObject({
    action: z.literal('cancel'),
    taskIds: bulkTaskIds,
    params: cancelTaskRequestSchema,
  }),
  z.strictObject({
    action: z.literal('complete'),
    taskIds: bulkTaskIds,
    params: completeTaskRequestSchema,
  }),
  z.strictObject({
    action: z.literal('add_watcher'),
    taskIds: bulkTaskIds,
    params: z.strictObject({ userId: uuidSchema }),
  }),
  z.strictObject({
    action: z.literal('move_project'),
    taskIds: bulkTaskIds,
    params: z.strictObject({
      projectId: uuidSchema.nullable(),
      milestoneId: uuidSchema.nullable().optional(),
    }),
  }),
]);
export type BulkTaskRequest = z.infer<typeof bulkTaskRequestSchema>;

export const bulkItemErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
});
export const bulkTaskResponseSchema = z.object({
  results: z.array(
    z.object({
      taskId: z.string(),
      ok: z.boolean(),
      error: bulkItemErrorSchema.nullable(),
      task: taskDtoSchema.nullable(),
    }),
  ),
});
export type BulkTaskResponse = z.infer<typeof bulkTaskResponseSchema>;

/**
 * A message that is a LIST becomes many tasks (D-1120).
 *
 * The single-draft endpoint above answers "what task is this message?". This one answers "this
 * message is fifty lines of work — make them all", which is how people actually hand over a
 * sprint, a launch checklist or the output of a meeting.
 *
 * Two steps, never one. `GET` returns what the server would create, each line already run through
 * the same quick-add grammar as `/task` so `@priya`, `friday` and `p1` are understood; `POST`
 * creates exactly the rows the caller sends back. A single-shot "make tasks from this" would be a
 * button that silently invents fifty assignments and due dates, and the first thing anybody would
 * do is undo it.
 */
export const TASK_LIST_MAX_ITEMS = 100;

export const taskListItemDraftSchema = z.object({
  /** The line as written, before parsing — shown so somebody can see what a row came from. */
  text: z.string(),
  draft: taskDraftDtoSchema,
  warnings: z.array(quickAddWarningDtoSchema),
  /** Nesting depth in the original list; 0 for a top-level line. */
  depth: z.number().int().min(0),
});
export type TaskListItemDraft = z.infer<typeof taskListItemDraftSchema>;

export const parseTaskLinesResponseSchema = z.object({
  items: z.array(taskListItemDraftSchema),
  /** How many lines were found beyond `TASK_LIST_MAX_ITEMS` and therefore not offered. */
  truncated: z.number().int().min(0),
});
export type ParseTaskLinesResponse = z.infer<typeof parseTaskLinesResponseSchema>;

export const taskListPreviewResponseSchema = z.object({
  items: z.array(taskListItemDraftSchema),
  /** The conversation the tasks would be linked to, and the thread if the message is in one. */
  conversationId: uuidSchema,
  threadRootId: uuidSchema.nullable(),
  /**
   * True when the lines came from hard breaks rather than real list nodes.
   *
   * The UI says so, because that reading is a guess at intent and the person should get a chance
   * to disagree before fifty rows are created.
   */
  fromParagraphs: z.boolean(),
  /** How many lines were found beyond `TASK_LIST_MAX_ITEMS` and therefore not offered. */
  truncated: z.number().int().min(0),
});
export type TaskListPreviewResponse = z.infer<typeof taskListPreviewResponseSchema>;

/**
 * `POST /tasks/parse-lines`: the composer's multi-line read (D-1217).
 *
 * Somebody pastes ten lines of work into the task box — the output of a call, a handover, a list
 * off a whiteboard — and each line should become its own task without ten trips through a dialog.
 * This is the same grammar as `/tasks/parse`, applied per line, and it creates nothing: the
 * composer shows the rows, the person edits them, and `POST /tasks/batch` makes the ones they
 * keep. Blank lines and list markers (`-`, `*`, `1.`, `[ ]`) are stripped; anything past
 * `TASK_LIST_MAX_ITEMS` is reported in `truncated` rather than silently dropped.
 */
export const parseTaskLinesRequestSchema = z.strictObject({
  text: z
    .string()
    .min(1)
    .max(L.quickAddMax * 100),
});
export type ParseTaskLinesRequest = z.infer<typeof parseTaskLinesRequestSchema>;

export const createTasksFromListRequestSchema = z.strictObject({
  /** The rows to create, in order — what the caller kept and edited from the preview. */
  items: z
    .array(
      z.strictObject({
        title: z.string().trim().min(1).max(L.titleMax),
        ownerUserId: uuidSchema.nullable().default(null),
        dueDate: localDateSchema.nullable().default(null),
        dueTime: clockTimeSchema.nullable().default(null),
        priority: taskPrioritySchema.nullable().default(null),
        projectId: uuidSchema.nullable().default(null),
      }),
    )
    .min(1)
    .max(TASK_LIST_MAX_ITEMS),
  /** Required when a task is for somebody else, exactly as a single task requires one. */
  definitionOfDone: z.string().trim().max(L.definitionOfDoneMax).nullable().default(null),
});
export type CreateTasksFromListRequest = z.infer<typeof createTasksFromListRequestSchema>;

/**
 * One result per row, in request order — the same shape as `POST /tasks/bulk`.
 *
 * Each row is created in its OWN transaction under its own rules, so one refusal (the upward
 * guard, a project the owner cannot see) never costs the other forty-nine. Partial success is the
 * expected outcome, not an error.
 */
export const createTasksFromListResponseSchema = z.object({
  results: z.array(
    z.object({
      title: z.string(),
      ok: z.boolean(),
      task: taskDtoSchema.optional(),
      error: bulkItemErrorSchema.nullable().optional(),
    }),
  ),
  created: z.number().int().min(0),
});

/**
 * `POST /tasks/batch` (D-1217): the same rows, from the composer rather than from a message.
 *
 * Identical body and answer to the message's list — the only difference is that these tasks have
 * no message to point back at, because nobody wrote them in a conversation. Sharing the schema is
 * deliberate: the two paths must stay the same shape, since they are the same promise (every row
 * is its own transaction, partial success is normal).
 */
export const createTaskBatchRequestSchema = createTasksFromListRequestSchema;
export type CreateTaskBatchRequest = z.infer<typeof createTaskBatchRequestSchema>;
export const createTaskBatchResponseSchema = createTasksFromListResponseSchema;
export type CreateTaskBatchResponse = z.infer<typeof createTaskBatchResponseSchema>;
export type CreateTasksFromListResponse = z.infer<typeof createTasksFromListResponseSchema>;

// ================================================================ upward-guard exceptions (admin)

export const delegationGuardExceptionDtoSchema = z.object({
  id: uuidSchema,
  assignerUserId: uuidSchema,
  assigneeUserId: uuidSchema,
  reason: z.string(),
  expiresAt: isoDateTimeSchema.nullable(),
  createdByUserId: uuidSchema,
  createdAt: isoDateTimeSchema,
  revokedAt: isoDateTimeSchema.nullable(),
  revokedByUserId: uuidSchema.nullable(),
  revokeReason: z.string().nullable(),
  /** Not revoked and not expired now. */
  active: z.boolean(),
});
export type DelegationGuardExceptionDto = z.infer<typeof delegationGuardExceptionDtoSchema>;

export const exceptionIdParamsSchema = z.object({ exceptionId: uuidSchema });
export const createDelegationGuardExceptionRequestSchema = z.strictObject({
  assignerUserId: uuidSchema,
  assigneeUserId: uuidSchema,
  /** ≥ 10 characters (CHECK). */
  reason: multiLineText(10, L.reasonMax),
  expiresAt: isoDateTimeSchema.nullable().optional(),
});
export const revokeDelegationGuardExceptionRequestSchema = z.strictObject({
  reason: taskReasonSchema,
});
export const listDelegationGuardExceptionsQuerySchema = paginationQuerySchema.extend({
  assignerUserId: uuidSchema.optional(),
  assigneeUserId: uuidSchema.optional(),
  includeRevoked: queryBooleanSchema.optional(),
});
export const delegationGuardExceptionListResponseSchema = paginated(
  delegationGuardExceptionDtoSchema,
);
export const delegationGuardExceptionResponseSchema = z.object({
  exception: delegationGuardExceptionDtoSchema,
});

// ================================================================ offboarding (SPEC §6.10, D-023)

export const offboardingPreviewResponseSchema = z.object({
  userId: uuidSchema,
  /** Proposed, accepted, in progress or blocked tasks they own (decision requests excluded). */
  openOwnedTasks: z.array(taskSummaryDtoSchema),
  /** Open decision requests where they are the decider. */
  pendingDecisions: z.array(taskSummaryDtoSchema),
  /** Tasks and decision requests they delegated that are open or Done-unverified. */
  openDelegatedTasks: z.array(taskSummaryDtoSchema),
  /** Open checklist items they own on other people's tasks. */
  checklistItems: z.array(
    z.object({ taskId: uuidSchema, taskNumber: z.string(), itemId: uuidSchema, title: z.string() }),
  ),
  recurrences: z.array(
    z.object({ id: uuidSchema, title: z.string(), role: z.enum(['owner', 'delegator']) }),
  ),
  projects: z.array(z.object({ id: uuidSchema, name: z.string() })),
  /** Phase 7 (meeting series they chair) — always empty until then. */
  meetingSeries: z.array(z.object({ id: uuidSchema, name: z.string() })),
  /** Their manager: the default new delegator. */
  suggestedDelegatorUserId: uuidSchema.nullable(),
  /** True when nothing blocks deactivation. */
  clear: z.boolean(),
});
export type OffboardingPreviewResponse = z.infer<typeof offboardingPreviewResponseSchema>;

export const offboardRequestSchema = z.strictObject({
  tasks: z
    .array(
      z.discriminatedUnion('action', [
        z.strictObject({
          taskId: uuidSchema,
          action: z.literal('reassign'),
          newOwnerUserId: uuidSchema,
          definitionOfDone: definitionOfDoneSchema.nullable().optional(),
        }),
        z.strictObject({
          taskId: uuidSchema,
          action: z.literal('cancel'),
          reason: taskReasonSchema,
        }),
      ]),
    )
    .max(2_000),
  /** New delegator for the tasks they delegated (default: their manager). */
  delegatorTransfer: z
    .strictObject({
      defaultUserId: uuidSchema.nullable(),
      overrides: z.array(z.strictObject({ taskId: uuidSchema, userId: uuidSchema })).max(2_000),
    })
    .optional(),
  /** Their open checklist items on other tasks: clear the owner or give them to someone. */
  checklistItems: z
    .union([
      z.literal('clear_owner'),
      z.array(z.strictObject({ itemId: uuidSchema, ownerUserId: uuidSchema.nullable() })),
    ])
    .optional(),
  recurrences: z
    .array(
      z.discriminatedUnion('action', [
        z.strictObject({
          recurrenceId: uuidSchema,
          action: z.literal('reassign'),
          newOwnerUserId: uuidSchema,
        }),
        z.strictObject({ recurrenceId: uuidSchema, action: z.literal('end') }),
      ]),
    )
    .optional(),
  projects: z
    .array(z.strictObject({ projectId: uuidSchema, newLeadUserId: uuidSchema }))
    .optional(),
  /** Stored in the audit metadata. */
  reason: multiLineText(1, 500).optional(),
});
export type OffboardRequest = z.infer<typeof offboardRequestSchema>;

export const offboardResponseSchema = z.object({
  member: memberDtoSchema,
  results: z.array(
    z.object({ taskId: uuidSchema, ok: z.boolean(), error: bulkItemErrorSchema.nullable() }),
  ),
});
export type OffboardResponse = z.infer<typeof offboardResponseSchema>;

// ================================================================ follow-up automation settings

/** `GET/PATCH /admin/settings/tasks`: the workspace `tasks.*` settings (PLAN §3.2). */
export const taskSettingsDtoSchema = z.object({
  acceptanceReminderWorkingHours: z.number().positive(),
  acceptanceDelegatorNotifyWorkingHours: z.number().positive(),
  pushbackReminderWorkingHours: z.number().positive(),
  verificationReminderWorkingHours: z.number().positive(),
  decisionDeadlineReminderWorkingHours: z.number().positive(),
  morningDigestTime: clockTimeSchema,
  dueTodayNudgeTime: clockTimeSchema,
  slippingSlipCount: z.int().min(1),
  slippingOverdueDays: z.int().min(0),
  escalateDelegatorAfterOverdueDays: z.int().min(1),
  escalateManagerAfterOverdueDays: z.int().min(1),
});
export type TaskSettingsDto = z.infer<typeof taskSettingsDtoSchema>;
export const updateTaskSettingsRequestSchema = taskSettingsDtoSchema
  .partial()
  .strict()
  .refine((body) => Object.keys(body).length > 0, 'Change at least one setting');

/** `GET/PUT /admin/departments/:departmentId/task-settings` (null = inherit the workspace). */
export const departmentTaskSettingsDtoSchema = z.object({
  departmentId: uuidSchema,
  escalateDelegatorAfterOverdueDays: z.int().min(1).max(30),
  escalateManagerAfterOverdueDays: z.int().min(1),
  escalateSelfTasksToManager: z.boolean(),
  dueTodayNudgeTime: clockTimeSchema.nullable(),
  acceptanceReminderWorkingHours: z.int().min(1).max(1000).nullable(),
  defaultTaskVisibility: taskVisibilitySchema,
  updatedAt: isoDateTimeSchema.nullable(),
  /** What applies after merging with the workspace settings (`resolveTaskSettings`). */
  effective: taskSettingsDtoSchema.extend({
    escalateSelfTasksToManager: z.boolean(),
    defaultTaskVisibility: taskVisibilitySchema,
  }),
});
export type DepartmentTaskSettingsDto = z.infer<typeof departmentTaskSettingsDtoSchema>;
export const putDepartmentTaskSettingsRequestSchema = z.strictObject({
  escalateDelegatorAfterOverdueDays: z.int().min(1).max(30),
  escalateManagerAfterOverdueDays: z.int().min(1).max(365),
  escalateSelfTasksToManager: z.boolean(),
  dueTodayNudgeTime: clockTimeSchema.nullable(),
  acceptanceReminderWorkingHours: z.int().min(1).max(1000).nullable(),
  defaultTaskVisibility: taskVisibilitySchema,
});

/** A follow-up kind as recorded in `task_followups` (read-only; used by admin diagnostics). */
export const followupKindSchema = z.enum(FOLLOWUP_KINDS);
